import React, {useCallback, useEffect, useState} from "react";
import {Form, Formik} from "formik";
import FormField from "./FormField";
import Checkbox from "./Checkbox";
import CheckboxesFieldGroup from "./CheckboxesFieldGroup";
import Button from "./Button";
import LocationIcon from "./icons/LocationIcon";
import FormSelect from "./FormSelect";
import PlacesAutoCompleteInput from "./PlacesAutoCompleteInput";
import UserLocationInfo from "./UserLocationInfo";
import {FiltrationFormValuesT} from "../types/FiltrationFormValues";
import {APILoadingStatus, useApiLoadingStatus, useMap, useMapsLibrary} from "@vis.gl/react-google-maps";
import {useSearchParams} from "react-router";
import {useErrorBoundary} from "react-error-boundary";
import generateFiltrationFormValidationSchema from "../functions/generateFiltrationFormValidationSchema";
import FormFieldError from "./FormFieldError";
import getLatLngLiteralForPlace from "../functions/getLatLngLiteralForPlace";
import {useDispatch, useSelector} from "react-redux";
import {AppDispatch, RootState} from "../store";
import {storeSearchCircleSliceActions} from "../store/storeSearchCircleSlice";
import locationDefinitionModeSlice from "../store/locationDefinitionModeSlice";

const FiltrationForm: React.FC = () => {

    const googleMapsAPILoadingStatus = useApiLoadingStatus();
    const {showBoundary} = useErrorBoundary<Error>();
    const placesLib = useMapsLibrary("places");

    const isSearchFormLoading = useSelector((state: RootState) => state.isSearchFormLoadingSlice);
    const storeLocatorSettings = useSelector((state: RootState) => state.storeLocatorSettingsSlice);

    const storeSearchCenterCoords = useSelector((state: RootState) => state.storeSearchCircleSlice.storeSearchCenterCoords);
    const searchResults = useSelector((state: RootState) => state.searchResultsSlice);

    const dispatch = useDispatch<AppDispatch>();

    const [isDefiningUserLocation, setIsDefiningUserLocation] = useState<boolean>(false);

    const searchLocationDefinitionMode = useSelector((state: RootState) => state.locationDefinitionModeSlice);

    const [manualSearchLocationDefPlaceID, setManualSearchLocationDefPlaceID] = useState<string | null>(null);

    const [initialFiltrationFormValues, setInitialFiltrationFormValues] = useState<FiltrationFormValuesT>({
        store_search_place: null,
        store_search_radius: null,
        blj_store_locator_product_type: []
    });

    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {

        const locationDefinitionMode = searchParams.get("location_definition_mode");
        if (locationDefinitionMode === null || placesLib === null) {
            return;
        }

        const storeSearchRadius = searchParams.get("store_search_radius");
        const storeProductTypes = searchParams.get("store_product_types");

        if (locationDefinitionMode === "manual") {

            const storeSearchPlace = searchParams.get("store_search_place_id");
            if (storeSearchPlace === null) {
                return;
            }

            // Getting the Place Name by Place ID (value of the store_search_place)
            const placeObj = new google.maps.places.Place({
                id: storeSearchPlace,
                requestedLanguage: "en"
            });

            placeObj.fetchFields({
                fields: ["displayName"],
            }).then((placeDetails) => {

                if (!placeDetails.place.displayName) {
                    throw new Error("No place name found in the place details.");
                }

                setManualSearchLocationDefPlaceID(storeSearchPlace);

                setInitialFiltrationFormValues({
                    store_search_place: {
                        label: placeDetails.place.displayName,
                        value: storeSearchPlace
                    },

                    store_search_radius: storeSearchRadius ? {
                        label: `${storeSearchRadius} miles`,
                        value: storeSearchRadius
                    } : null,

                    blj_store_locator_product_type: storeProductTypes ? storeProductTypes.split(",") : []
                });

            }).catch((error) => {

                console.error("Error fetching place details:", error);

                // Show an error message to the user
                showBoundary(error);

            });
        }

        if (locationDefinitionMode === "gps") {
            setInitialFiltrationFormValues({
                store_search_place: null,
                store_search_radius: storeSearchRadius ? {
                    label: `${storeSearchRadius} miles`,
                    value: storeSearchRadius
                } : null,

                blj_store_locator_product_type: storeProductTypes ? storeProductTypes.split(",") : []
            });
        }

    }, [placesLib, searchParams, showBoundary]);

    const mapObject = useMap('blj-store-locator-map');
    const checkboxesPerColumn = storeLocatorSettings.productTypeTerms.length / 2;
    const isFormFieldDisabled =
        isDefiningUserLocation ||
        isSearchFormLoading ||
        googleMapsAPILoadingStatus === APILoadingStatus.LOADING ||
        googleMapsAPILoadingStatus === APILoadingStatus.FAILED ||
        googleMapsAPILoadingStatus === APILoadingStatus.AUTH_FAILURE;

    // Checking if Google Maps API is loaded correctly and showing an error message if not
    useEffect(() => {
        switch (googleMapsAPILoadingStatus) {
            case APILoadingStatus.FAILED:
                showBoundary(new Error("Google Maps API failed to load."));
                break;

            case APILoadingStatus.AUTH_FAILURE:
                showBoundary(new Error("Google Maps API failed to load due to authentication failure (check your API Key)."));
                break;
        }

    }, [googleMapsAPILoadingStatus, showBoundary]);

    // Setting the map bounds to fit all the search results
    useEffect(() => {

        if (mapObject !== null && searchResults.length > 0) {

            const bounds = new google.maps.LatLngBounds();
            searchResults.forEach((searchResult) => {
                bounds.extend({
                    lat: searchResult.store.storeLocation.lat, lng: searchResult.store.storeLocation.lng
                });
            });

            // If there is only one search result, set the center of the map to this result
            if (searchResults.length === 1) {
                mapObject.setCenter({
                    lat: searchResults[0].store.storeLocation.lat,
                    lng: searchResults[0].store.storeLocation.lng
                });
            } else {
                mapObject.fitBounds(bounds);
            }

        }

    }, [mapObject, searchResults]);

    const defineUserLocation = () => {

        setIsDefiningUserLocation(true);

        // Getting the current user location
        navigator.geolocation.getCurrentPosition(
            // Success callback
            (position) => {

                setIsDefiningUserLocation(false);
                dispatch(locationDefinitionModeSlice.actions.setLocationDefinitionMode("gps"));

                dispatch(storeSearchCircleSliceActions.setStoreSearchCenterCoords({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                }));

            },

            // Error callback
            (error) => {

                setIsDefiningUserLocation(false);

                dispatch(locationDefinitionModeSlice.actions.setLocationDefinitionMode("manual"));

                dispatch(storeSearchCircleSliceActions.setStoreSearchCenterCoords(undefined));

                // TODO: Maybe make an error message more user-friendly
                switch (error.code) {

                    case error.PERMISSION_DENIED:
                        alert("You have denied the request for Geolocation.");
                        break;

                    case error.POSITION_UNAVAILABLE:
                        alert("Location information is unavailable.");
                        break;

                    case error.TIMEOUT:
                        alert("The request to get user location timed out.");
                        break;

                }

            }
        );

    }

    const submitHandler = useCallback((formValues: FiltrationFormValuesT) => {

        // Checking if the Location Definition mode is manual and the place ID is defined
        if (searchLocationDefinitionMode === "manual") {

            if (manualSearchLocationDefPlaceID !== null) {

                getLatLngLiteralForPlace(placesLib, manualSearchLocationDefPlaceID)
                    .then((placeLocation) => {

                        if (placeLocation !== null) {

                            dispatch(storeSearchCircleSliceActions.setStoreSearchCenterCoords(placeLocation));

                            // Updating the Search Params
                            setSearchParams((searchParams) => {

                                if (formValues.store_search_radius === null || formValues.blj_store_locator_product_type.length === 0) {
                                    return searchParams;
                                }

                                const productTypesString = formValues.blj_store_locator_product_type.join(",");

                                searchParams.set("location_definition_mode", searchLocationDefinitionMode);
                                searchParams.set("store_search_place_id", manualSearchLocationDefPlaceID);
                                searchParams.set("store_search_radius", formValues.store_search_radius.value);
                                searchParams.set("store_product_types", productTypesString);

                                return searchParams;
                            });

                        } else {
                            throw new Error("Error fetching place location.");
                        }
                    })
                    .catch((error: Error) => {
                        console.error("Error fetching place location:", error);

                        // Show an error message to the user
                        showBoundary(error);
                    });
            }

        } else {

            // Search Params for the GPS location definition mode
            setSearchParams((searchParams) => {

                if (storeSearchCenterCoords === undefined || formValues.store_search_radius === null || formValues.blj_store_locator_product_type.length === 0) {
                    return searchParams;
                }

                const productTypesString = formValues.blj_store_locator_product_type.join(",");

                searchParams.set("location_definition_mode", searchLocationDefinitionMode);
                searchParams.set("store_search_user_location", `${storeSearchCenterCoords.lat},${storeSearchCenterCoords.lng}`);
                searchParams.set("store_search_radius", formValues.store_search_radius.value);
                searchParams.set("store_product_types", productTypesString);

                return searchParams;
            });

        }

    }, [
        storeSearchCenterCoords,
        manualSearchLocationDefPlaceID,
        searchLocationDefinitionMode,
        getLatLngLiteralForPlace,
        dispatch,
        showBoundary,
        setSearchParams
    ]);

    return (
        <Formik
            key={JSON.stringify(initialFiltrationFormValues)}
            initialValues={initialFiltrationFormValues}
            validationSchema={generateFiltrationFormValidationSchema(searchLocationDefinitionMode === "manual")}
            enableReinitialize={true}
            onSubmit={submitHandler}
        >
            <Form className="blj-store-locator-form">

                <FormField
                    label="Address or Zip/Postal Code"
                    id={searchLocationDefinitionMode === "manual" ? "blj-store-locator-address" : undefined}
                    name="store_search_place"
                    labelButton={
                        <Button
                            text={"Use my location"}
                            variant={"secondary"}
                            iconElement={<LocationIcon/>}
                            type="button"
                            onClick={defineUserLocation}
                            disabled={isFormFieldDisabled}
                        />
                    }
                >
                    {searchLocationDefinitionMode === "manual" ? (
                        <PlacesAutoCompleteInput
                            id="blj-store-locator-address"
                            name="store_search_place"
                            disabled={isFormFieldDisabled}
                            setManualSearchLocationDefPlaceID={setManualSearchLocationDefPlaceID}
                        />
                    ) : (
                        storeSearchCenterCoords ? (
                            <UserLocationInfo
                                userLocation={storeSearchCenterCoords}
                                unsetUserLocationCallback={() => {
                                    dispatch(locationDefinitionModeSlice.actions.setLocationDefinitionMode("manual"));
                                }}
                            />
                        ) : (
                            <FormFieldError errorMessage={"User location is not defined."}/>
                        )
                    )}
                </FormField>

                <FormField
                    label="Within"
                    id="blj-store-locator-radius"
                    name="store_search_radius"
                >
                    <FormSelect
                        name="store_search_radius"
                        id="blj-store-locator-radius"
                        options={[
                            {
                                label: "5 miles",
                                value: "5"
                            },
                            {
                                label: "10 miles",
                                value: "10"
                            },
                            {
                                label: "25 miles",
                                value: "25"
                            },
                            {
                                label: "50 miles",
                                value: "50"
                            },
                            {
                                label: "100 miles",
                                value: "100"
                            }
                        ]}
                        isDisabled={isFormFieldDisabled}
                    />
                </FormField>

                {storeLocatorSettings.productTypeTerms.length > 0 && (
                    <CheckboxesFieldGroup
                        name="blj_store_locator_product_type"
                    >
                        <div className="blj-store-locator-form-checkboxes">

                            <div className="blj-store-locator-form-checkboxes-col">

                                <div className="blj-store-locator-form-checkboxes-group">
                                    {storeLocatorSettings.productTypeTerms.map(
                                        (productTypeFieldValue, index) => {
                                            if (index < checkboxesPerColumn) {

                                                return (
                                                    <Checkbox
                                                        key={index}
                                                        label={productTypeFieldValue.name}
                                                        name="blj_store_locator_product_type"
                                                        value={productTypeFieldValue.term_id.toString()}
                                                        id={`checkbox_${productTypeFieldValue.term_id.toString()}`}
                                                        defaultChecked={initialFiltrationFormValues.blj_store_locator_product_type.includes(productTypeFieldValue.term_id.toString())}
                                                        disabled={isFormFieldDisabled}
                                                    />
                                                );

                                            }
                                        }
                                    )}
                                </div>

                            </div>

                            <div className="blj-store-locator-form-checkboxes-col">
                                <div className="blj-store-locator-form-checkboxes-col">

                                    <div className="blj-store-locator-form-checkboxes-group">

                                        {storeLocatorSettings.productTypeTerms.map(
                                            (productTypeFieldValue, index) => {
                                                if (index >= checkboxesPerColumn) {
                                                    return (
                                                        <Checkbox
                                                            key={index + checkboxesPerColumn}
                                                            label={productTypeFieldValue.name}
                                                            name="blj_store_locator_product_type"
                                                            value={productTypeFieldValue.term_id.toString()}
                                                            id={`checkbox_${productTypeFieldValue.term_id.toString()}`}
                                                            defaultChecked={initialFiltrationFormValues.blj_store_locator_product_type.includes(productTypeFieldValue.term_id.toString())}
                                                            disabled={isFormFieldDisabled}
                                                        />
                                                    );
                                                }
                                            }
                                        )}
                                    </div>

                                </div>
                            </div>
                        </div>
                    </CheckboxesFieldGroup>
                )}

                <Button
                    type="submit"
                    variant="primary"
                    text={isFormFieldDisabled ? "Loading..." : "Find Stores"}
                    disabled={isFormFieldDisabled}
                />

            </Form>
        </Formik>
    );
}

export default FiltrationForm;